"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { useWatch } from "react-hook-form";
import { CircleCheck, Loader, Sparkles, Calendar, User, Mail, Phone, Info } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
export default function LetsStartYourTransformation() {
  return <section data-section-id="222851" id="lets-talk-finance" className="w-full py-16 md:py-24 bg-cover bg-center bg-no-repeat !bg-[url(https://slafinance.com.au/wp-content/uploads/2026/05/admin-ajax.png)] bg-background/90 bg-blend-overlay text-foreground">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default text-primary mb-4 tracking-tight">
            Secure Your Financial Future
          </h2>
          <p className="text-lg text-muted-foreground font-default max-w-2xl mx-auto">
            Partner with SLA Finance Group for bespoke lending solutions. Fill out the details below to connect with a senior finance specialist and schedule your free consultation.
          </p>
        </div>

        <FormProvider formKey="talk_finance_form_get_in_touch" formId="talk_finance_form_get_in_touch" sectionName="lets-talk-finance" defaultValues={{
        full_name: "",
        email: "",
        phone: "",
        service_type: "",
        preferred_date: "",
        message: ""
      }} validationRules={{
        full_name: {
          required: "Full Name is required"
        },
        email: {
          required: "Professional Email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Please enter a valid email address"
          }
        },
        phone: {
          required: "Contact Number is required",
          pattern: {
            value: /^[0-9+\-\s()]*$/,
            message: "Please enter a valid phone number"
          }
        },
        service_type: {
          required: "Please select a finance solution"
        }
      }} submitText="Book Consultation" successMessage="Your consultation request has been received. A senior finance specialist from SLA Finance Group will contact you shortly to discuss your lending strategy.">
          <TransformationFormContent />
        </FormProvider>
      </div>
    </section>;
}
function TransformationFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const formValues = useWatch({
    control
  });
  if (isSubmitted && !submitError) {
    return <Card className="max-w-2xl mx-auto border-border bg-card shadow-xl rounded-lg">
        <CardContent className="pt-12 pb-12 flex flex-col items-center text-center space-y-6">
          <div className="h-16 w-16 bg-accent/50 rounded-full flex items-center justify-center mb-4">
            <CircleCheck className="h-8 w-8 text-primary" />
          </div>
          <CardTitle className="text-2xl font-default font-bold text-primary">Inquiry Received!</CardTitle>
          <p className="text-muted-foreground font-default text-lg max-w-md">
            {successMessage}
          </p>
          <Button onClick={resetForm} variant="outline" className="mt-6 border-primary text-primary hover:bg-primary/5 rounded-lg">
            Submit Another Request
          </Button>
        </CardContent>
      </Card>;
  }
  return <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
      {}
      <div className="lg:col-span-7 xl:col-span-8">
        <Card className="shadow-md border-border/50 rounded-lg">
          <CardHeader className="border-b border-border/50 bg-muted/20 pb-6">
            <CardTitle className="text-xl font-default font-semibold flex items-center gap-2 text-primary">
              <Info className="h-5 w-5 text-ring" />
              Application Details
            </CardTitle>
            <CardDescription className="font-default">
              Provide your contact information and the financial services you are interested in.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <Form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField control={control} name="full_name" render={({
                field
              }) => <FormItem>
                      <FormLabel className="font-default text-primary">Full Name <span className="text-destructive">*</span></FormLabel>
                      <FormControl>
                        <Input placeholder="Alexander Sterling" className="rounded-lg border-muted focus:ring-ring" {...field} />
                      </FormControl>
                      <FormDescription className="font-default text-xs">Enter your legal first and last name.</FormDescription>
                      <FormMessage />
                    </FormItem>} />

                <FormField control={control} name="email" render={({
                field
              }) => <FormItem>
                      <FormLabel className="font-default text-primary">Professional Email <span className="text-destructive">*</span></FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="alexander@sterling.com" className="rounded-lg border-muted focus:ring-ring" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField control={control} name="phone" render={({
                field
              }) => <FormItem>
                      <FormLabel className="font-default text-primary">Contact Number <span className="text-destructive">*</span></FormLabel>
                      <FormControl>
                        <Input type="tel" placeholder="+61 2 9123 4567" className="rounded-lg border-muted focus:ring-ring" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />

                <FormField control={control} name="preferred_date" render={({
                field
              }) => <FormItem>
                      <FormLabel className="font-default text-primary">Preferred Consultation Date</FormLabel>
                      <FormControl>
                        <Input type="date" className="rounded-lg border-muted focus:ring-ring" {...field} />
                      </FormControl>
                      <FormDescription className="font-default text-xs">If you have a specific time in mind, let us know.</FormDescription>
                      <FormMessage />
                    </FormItem>} />
              </div>

              <FormField control={control} name="service_type" render={({
              field
            }) => <FormItem>
                    <FormLabel className="font-default text-primary">Finance Solution Required <span className="text-destructive">*</span></FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger className="w-full rounded-lg border-muted focus:ring-ring">
                          <SelectValue placeholder="Select a solution" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="rounded-lg">
                        <SelectItem value="Home Loans">Home Loans</SelectItem>
                        <SelectItem value="Business Finance">Business Finance</SelectItem>
                        <SelectItem value="Asset & Equipment Finance">Asset & Equipment Finance</SelectItem>
                        <SelectItem value="Refinancing Strategy">Refinancing Strategy</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="message" render={({
              field
            }) => <FormItem>
                    <FormLabel className="font-default text-primary">Financial Objectives</FormLabel>
                    <FormControl>
                      <Textarea placeholder="Briefly describe your goals or any specific challenges you're facing." className="min-h-[120px] resize-y rounded-lg border-muted focus:ring-ring" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />

              {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                  <p className="text-destructive text-sm font-medium font-default">{submitError}</p>
                </div>}

              <div className="pt-4 flex justify-end">
                <Button type="submit" size="lg" disabled={isSubmitting} className="w-full md:w-auto min-w-[200px] bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg font-default font-semibold shadow-md">
                  {isSubmitting ? <>
                      <Loader className="mr-2 h-5 w-5 animate-spin" />
                      Processing...
                    </> : <>
                      Book Consultation
                      <Sparkles className="ml-2 h-4 w-4 text-ring" />
                    </>}
                </Button>
              </div>
            </Form>
          </CardContent>
        </Card>
      </div>

      {}
      <div className="lg:col-span-5 xl:col-span-4 sticky top-24">
        <Card className="shadow-xl border-ring/10 bg-accent/30 overflow-hidden rounded-lg">
          <div className="h-2 w-full bg-ring" />
          <CardHeader>
            <CardTitle className="text-xl font-default font-bold text-primary flex items-center gap-2">
              Inquiry Summary
            </CardTitle>
            <CardDescription className="font-default">
              Your customised finance request details.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            
            {}
            <div className="space-y-2">
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest font-default">Selected Solution</p>
              <div className="flex items-start gap-3">
                <div className="mt-0.5 bg-background p-2 rounded-lg shadow-sm border border-border/50">
                  <Sparkles className="h-4 w-4 text-ring" />
                </div>
                <div>
                  <p className="font-semibold text-primary font-default">
                    {formValues.service_type || "No solution selected"}
                  </p>
                  <p className="text-sm text-muted-foreground font-default">
                    {formValues.service_type ? "Professional brokerage tailored to your goals." : "Please choose a finance solution."}
                  </p>
                </div>
              </div>
            </div>

            <div className="h-px w-full bg-border/50" />

            {}
            <div className="space-y-2">
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest font-default">Proposed Schedule</p>
              <div className="flex items-center gap-3">
                <div className="bg-background p-2 rounded-lg shadow-sm border border-border/50">
                  <SafeHydrate><Calendar className="h-4 w-4 text-ring" /></SafeHydrate>
                </div>
                <p className="font-semibold text-primary font-default">
                  {formValues.preferred_date ? new Date(formValues.preferred_date).toLocaleDateString(undefined, {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                }) : "Flexible Date"}
                </p>
              </div>
            </div>

            <div className="h-px w-full bg-border/50" />

            {}
            <div className="space-y-3">
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest font-default">Applicant Contact</p>
              
              <div className="flex items-center gap-3">
                <User className="h-4 w-4 text-muted-foreground" />
                <p className="text-sm text-primary font-default truncate">
                  {formValues.full_name || "Name not provided"}
                </p>
              </div>
              
              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <p className="text-sm text-primary font-default truncate">
                  {formValues.email || "Email not provided"}
                </p>
              </div>

              <div className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-muted-foreground" />
                <p className="text-sm text-primary font-default truncate">
                  {formValues.phone || "Phone not provided"}
                </p>
              </div>
            </div>

          </CardContent>
          <CardFooter className="bg-muted/30 border-t border-border/50 pt-4 pb-4">
            <p className="text-xs text-muted-foreground text-center w-full font-default">
              No financial obligation is required to request a consultation. Our team will contact you to confirm details.
            </p>
          </CardFooter>
        </Card>
      </div>
    </div>;
}